/* Gift codes + Discount codes admin panel.
   Two stacked sections:
   1) Discount Codes — percent-off promo codes (admin creates, applied at Stripe checkout).
   2) Gift Codes — historical record of admin-generated + £25-purchased 12-month gifts. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { runsLabel, CodeStatus, CodeDatesEditor, CodeBannerEditor, CodePlansEditor, PlanPercentInputs, planSummary } from "@/pages/admin/DiscountCodeDates";

const BLANK_PLANS = { "1mo": 20, annual: 20, family_annual: 20 };

export default function GiftCodesPanel() {
  return (
    <div className="space-y-12">
      <GiftStatsSection />
      <DiscountCodesSection />
      <GiftCodesSection />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Gift purchase stats — monthly sold / money taken / redeemed, £25 vs £100
// -----------------------------------------------------------------------------
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });

function GiftStatsSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/gift-stats").then((r) => setData(r.data)).catch(() => toast.error("Could not load gift stats"));
  }, []);
  const months = data?.months || [];
  return (
    <section data-testid="gift-stats-section">
      <p className="overline mb-2">Gift sales</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Gift purchase stats</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-2xl">Paid gifts by month — £25 12-month and £100 Family Plan. Money taken is after promo discounts.</p>
      {!data ? <p className="text-sm text-rf-ink-soft">Loading…</p> : months.length === 0 ? (
        <p className="text-sm text-rf-ink-soft" data-testid="gift-stats-empty">No gifts sold yet.</p>
      ) : (
        <div className="overflow-x-auto card-recipe !p-0">
          <table className="w-full text-sm" data-testid="gift-stats-table">
            <thead className="bg-rf-cream-alt text-left text-xs uppercase tracking-wider text-rf-ink-soft">
              <tr>
                <th className="p-3">Month</th><th className="p-3">£25 sold</th><th className="p-3">£100 sold</th>
                <th className="p-3">Money taken</th><th className="p-3">£25 redeemed</th><th className="p-3">£100 redeemed</th>
              </tr>
            </thead>
            <tbody>
              {months.map((r) => (
                <tr key={r.month} className="border-t border-rf-ink/5" data-testid={`gift-stats-row-${r.month}`}>
                  <td className="p-3 font-bold text-rf-ink">{monthLabel(r.month)}</td>
                  <td className="p-3">{r.sold_annual}</td>
                  <td className="p-3">{r.sold_family}</td>
                  <td className="p-3 font-semibold">{gbp(r.revenue_annual + r.revenue_family)}</td>
                  <td className="p-3">{r.redeemed_annual}</td>
                  <td className="p-3">{r.redeemed_family}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.redeemed_undated > 0 && (
        <p className="text-xs text-rf-ink-soft mt-2 italic" data-testid="gift-stats-undated">
          {data.redeemed_undated} older gift{data.redeemed_undated === 1 ? " was" : "s were"} redeemed before dates were recorded, so {data.redeemed_undated === 1 ? "it isn't" : "they aren't"} in a month above.
        </p>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Discount Codes
// -----------------------------------------------------------------------------
function DiscountCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    code: "",
    plan_percents: BLANK_PLANS,
    description: "",
    max_uses: "",
    starts_on: "",
    ends_on: "",
  });

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/discount-codes");
      setCodes(data || []);
    } catch (e) {
      toast.error("Could not load discount codes");
    }
  };

  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        plan_percents: form.plan_percents,
        description: form.description.trim(),
      };
      if (form.max_uses) body.max_uses = Number(form.max_uses);
      if (form.starts_on) body.starts_on = form.starts_on;
      if (form.ends_on) body.ends_on = form.ends_on;
      await api.post("/admin/discount-codes", body);
      toast.success(`Created ${body.code}`);
      setForm({ code: "", plan_percents: BLANK_PLANS, description: "", max_uses: "", starts_on: "", ends_on: "" });
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code");
    } finally { setLoading(false); }
  };

  const toggle = async (code, currentDisabled) => {
    try {
      await api.patch(`/admin/discount-codes/${code}`, { disabled: !currentDisabled });
      await reload();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      await api.delete(`/admin/discount-codes/${code}`);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-discount-codes-section">
      <p className="overline mb-2">Promo</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Discount codes</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-2xl">
        Percentage-off promo codes, set separately for Monthly, Annual and Family Annual. Buyers paste the code at checkout and that plan&apos;s discount is applied before Stripe (Annual and Family Annual discounts also apply to £25 / £100 gifts).
      </p>

      <form
        onSubmit={create}
        className="card-recipe !p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end"
        data-testid="discount-code-create-form"
      >
        <div className="sm:col-span-2">
          <label className="overline">Code</label>
          <input
            data-testid="discount-code-code"
            required
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            maxLength={32}
            placeholder="SUMMER25"
            className="input-rf mt-1 font-mono uppercase"
          />
        </div>
        <PlanPercentInputs value={form.plan_percents} onChange={(pp) => setForm({ ...form, plan_percents: pp })} />
        <p className="sm:col-span-2 lg:col-span-6 text-xs text-rf-ink-soft -mt-1" data-testid="discount-code-plans-hint">
          Set a different discount for each plan, e.g. 50% off Family Annual only. Leave a plan blank and the code won&apos;t work on it.
        </p>
        <div>
          <label className="overline">Max uses</label>
          <input
            data-testid="discount-code-max-uses"
            type="number"
            min={1}
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            placeholder="∞"
            className="input-rf mt-1"
          />
        </div>
        <div>
          <label className="overline">Starts (UK)</label>
          <input
            data-testid="discount-code-starts"
            type="date"
            value={form.starts_on}
            onChange={(e) => setForm({ ...form, starts_on: e.target.value })}
            className="input-rf mt-1"
          />
        </div>
        <div>
          <label className="overline">Ends (UK)</label>
          <input
            data-testid="discount-code-expires"
            type="date"
            value={form.ends_on}
            min={form.starts_on || undefined}
            onChange={(e) => setForm({ ...form, ends_on: e.target.value })}
            className="input-rf mt-1"
          />
        </div>
        <p className="sm:col-span-2 lg:col-span-6 text-xs text-rf-ink-soft -mt-1">
          Leave dates blank to run indefinitely. A code works from 00:00 on its start day to 23:59 on its end day, UK time, then switches off on its own.
        </p>
        <div className="sm:col-span-2 lg:col-span-6">
          <label className="overline">Description (internal)</label>
          <input
            data-testid="discount-code-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            maxLength={200}
            placeholder="Summer 2026 campaign — Instagram"
            className="input-rf mt-1"
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-6">
          <button
            data-testid="discount-code-create-btn"
            type="submit"
            disabled={loading}
            className="btn-amber !rounded-xl"
          >
            {loading ? "Creating…" : "Create code"}
          </button>
        </div>
      </form>

      <div className="card-recipe !p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="discount-code-table">
          <thead className="bg-rf-cream-alt">
            <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
              <th className="p-3">Code</th>
              <th className="p-3">Discount by plan</th>
              <th className="p-3">Uses</th>
              <th className="p-3">Runs</th>
              <th className="p-3">Description</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-rf-ink-soft italic">No discount codes yet.</td></tr>
            )}
            {codes.map((c) => (
              <tr key={c.code} className="border-t border-rf-ink/10" data-testid={`discount-code-row-${c.code}`}>
                <td className="p-3 font-mono font-bold">{c.code}</td>
                <td className="p-3 text-xs font-semibold" data-testid={`discount-code-plans-${c.code}`}>{planSummary(c)}</td>
                <td className="p-3">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="p-3 text-rf-ink-soft text-xs" data-testid={`discount-code-runs-${c.code}`}>{runsLabel(c)}</td>
                <td className="p-3 text-rf-ink-soft text-xs max-w-[260px] truncate">{c.description || "—"}</td>
                <td className="p-3"><CodeStatus c={c} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <CodePlansEditor c={c} onSaved={reload} />
                  <CodeBannerEditor c={c} onSaved={reload} />
                  <CodeDatesEditor c={c} onSaved={reload} />
                  <button
                    data-testid={`discount-code-toggle-${c.code}`}
                    onClick={() => toggle(c.code, c.disabled)}
                    className="text-xs font-bold text-rf-orange hover:underline mr-3"
                  >
                    {c.disabled ? "Enable" : "Disable"}
                  </button>
                  <button
                    data-testid={`discount-code-delete-${c.code}`}
                    onClick={() => remove(c.code)}
                    className="text-xs font-bold text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Gift Codes (12-month) — admin-generated + £25-purchased
// -----------------------------------------------------------------------------
function GiftCodesSection() {
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/gift-codes/list");
      setCodes(data || []);
    } catch { /* admin endpoint may 403 for non-admins */ }
  };

  useEffect(() => { reload(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/gift-codes", { tier: "annual" });
      setNewCode(data.code);
      toast.success(`Gift code created: ${data.code}`);
      await reload();
    } catch { toast.error("Could not create gift code"); }
  };

  return (
    <div data-testid="admin-gift-codes-section">
      <p className="overline mb-2">12-month gifts</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Gift codes</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-2xl">
        Includes both admin-generated codes (for comp / campaign) and codes purchased by users via the £25 Stripe gift flow.
      </p>

      <div className="card-recipe !p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <p className="font-display font-extrabold text-rf-ink">Issue a free 12-month gift code</p>
          <p className="text-xs text-rf-ink-soft">10-character alphanumeric. Single use. Redeemable on the Pricing page.</p>
        </div>
        <button data-testid="create-gift-code" onClick={create} className="btn-amber">Generate gift code</button>
        {newCode && (
          <div className="px-4 py-3 bg-rf-amber/10 rounded-xl">
            <p className="font-mono font-black text-xl text-rf-orange tracking-widest" data-testid="new-gift-code">{newCode}</p>
          </div>
        )}
      </div>

      <div className="card-recipe !p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="gift-code-table">
          <thead className="bg-rf-cream-alt">
            <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
              <th className="p-3">Code</th>
              <th className="p-3">Source</th>
              <th className="p-3">Recipient</th>
              <th className="p-3">Status</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-rf-ink-soft italic">No gift codes yet.</td></tr>
            )}
            {codes.map((g) => (
              <tr key={g.code} className="border-t border-rf-ink/10">
                <td className="p-3 font-mono font-bold">{g.code}</td>
                <td className="p-3 text-xs">
                  {g.purchased_session_id ? (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-rf-amber/20 text-rf-orange font-bold" title="Purchased via Stripe">Paid</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-rf-cream-alt text-rf-ink-soft font-bold">Admin</span>
                  )}
                </td>
                <td className="p-3 text-rf-ink-soft text-xs">
                  {g.recipient_email || g.redeemed_by || "—"}
                </td>
                <td className="p-3">
                  {g.redeemed ? (
                    <span className="text-[11px] font-bold uppercase tracking-wider text-rf-ink-soft">Redeemed</span>
                  ) : (
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Unused</span>
                  )}
                </td>
                <td className="p-3 text-rf-ink-soft text-xs">{g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
